import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import SMCLabel from '../../../components/Simple/SMCLabel';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import {useTranslation} from 'react-i18next';
import SMCButton from '../../../components/Simple/SMCButton';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Api from '../../../api/SMPFetchHelper';
import {getErrorCode, hasValue} from '../../../utils/utils';
import {LinearProgress} from '@mui/material';
import SMCPageLoading from '../../../components/core/pageComponents/SMCPageLoading';

const uri = 'data:application/vnd.ms-excel;base64,'
const template = '<html><head><style type="text/css">.WizTableHead { font-weight: bold; border: thin solid black; border: 1px solid black; border-collapse: collapse;}.WizTableSmallRow { border: thin solid black; border: 1px solid black; border-collapse: collapse;}</style></head><body>{innerHTML}</body></html>';
const base64 = chunk => window.btoa(unescape(encodeURIComponent(chunk)));
const format = (s, c) => s.replace(/{(\w+)}/g, (m, p) => c[p]);

const exportSummaryToExcel = (table, filename) => {
    if (!table.nodeType) table = document.getElementById(table)
    var ctx = { innerHTML: table.innerHTML }

const exportLink = document.createElement("a");
    exportLink.id = 'exportLink';
    document.body.appendChild(exportLink);
    document.getElementById('exportLink').href = uri + base64(format(template, ctx));
    document.getElementById('exportLink').download = filename;
    document.getElementById('exportLink').click();
};

function ImportSMPStepTwo({
    onCancel,
    pageParams
}) {
    const { t } = useTranslation();

    const {
        fileToImport,
        doOverrideExistingData,
        certificateHandling,
        doImportIntoAllDomains,
        domainToImportTo
    } = pageParams;

    const [taskId, setTaskId] = useState(null);
    const [inProgress, setInProgress] = useState(true);
    const [isFetchingSummary, setIsFetchingSummary] = useState(false);
    const [stopped, setStopped] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentNumber, setCurrentNumber] = useState(null);
    const [currentMax, setCurrentMax] = useState(null);
    const [currentDisplayType, setCurrentDisplayType] = useState(null);
    const [currentErrorCount, setCurrentErrorCount] = useState(null);
    const [processedObjectDataList, setProcessedObjectDataList] = useState(null);
    const [warningsOccurredDataList, setWarningsOccurredDataList] = useState(null);

    const canceled = useRef(false);

    const handleDismiss = useCallback(() => {
        canceled.current = true;
        onCancel();
    }, [onCancel]);

    const handleStop = useCallback(() => {
        if (window.confirm(t('SMC_SYSOPS_EXPWIZ_DO_STOP'))) {
            if (taskId) {
                Api.importDBFileStop(taskId);
            }
        }
    }, [taskId, t]);

    const startImport = useCallback(async () => {
        try {
            const importDBFileStartData = {
                isOverrideExistingData: doOverrideExistingData,
                serviceDomain: doImportIntoAllDomains ? null : domainToImportTo,
                certHandling: certificateHandling,
            }
            const resp = await Api.importDBFileStart(importDBFileStartData, fileToImport);
            const taskId = resp.data.data.taskID;
            setTaskId(taskId);
            const checkProgress = async () => {
                if (canceled.current === true) {
                    return;
                }
                try {
                    const resp = await Api.importDBFileGetProgress(taskId);
                    const respData = resp.data.data;
                    console.log(respData.message)
                    if (respData.message === 'STATE_RUNNING') {
                        const {total, currentDisplayType, currentMax, currentError} = respData;
                        setCurrentNumber(total);
                        setCurrentDisplayType(currentDisplayType);
                        setCurrentMax(currentMax);
                        setCurrentErrorCount(currentError);
                        setTimeout(checkProgress, 60);
                    }
                    else if (respData.message === 'STATE_DONE') {
                        setInProgress(false);
                        setIsFetchingSummary(true);
                        const resp = await Api.importDBFileDone(taskId);
                        const {processedObjectDataList, warningsOccurredDataList} = resp.data.data;
                        setProcessedObjectDataList(processedObjectDataList);
                        setWarningsOccurredDataList(warningsOccurredDataList);
                        setIsFetchingSummary(false);
                    }
                    else if (respData.message === 'STATE_STOPPED') {
                        setInProgress(false);
                        setStopped(true);
                    }
                } catch (e) {
                    setIsFetchingSummary(false);
                    setHasError(true);
                    const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
                    setErrorMessage(t(errorCode));
                }
            }
            checkProgress();
        } catch (e) {
            setHasError(true);
            const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
            setErrorMessage(t(errorCode));
        }
    }, [certificateHandling, doImportIntoAllDomains, doOverrideExistingData, domainToImportTo, fileToImport, t]);

    useEffect(() => {
        startImport();
    }, [startImport]);

    const headerText = useMemo(() => {
        if (hasError) {
            return t('SMC_SYSOPS_IMPWIZ_DO_DONE_TEXT_ERR');
        }
        else if (stopped) {
            return t('SMC_SYSOPS_IMPWIZ_DO_DONE_TEXT');
        }
        else if (inProgress) {
            return t('SMC_SYSOPS_IMPWIZ_DO_WORK_HEAD');
        }
        else {
            return t('SMC_SYSOPS_IMPWIZ_DO_DONE_HEAD');
        }
    }, [hasError, inProgress, stopped, t]);

    const body = useMemo(() => {
        if (hasError) {
            return <SMCLabel>{errorMessage}. Please try again</SMCLabel>
        }
        else if (stopped) {
            return <SMCLabel>{t('SMC_SYSOPS_IMPWIZ_DO_STOPPED_TEXT')}</SMCLabel>
        }
        else if (inProgress) {
            if (hasValue(currentNumber) && currentMax && currentDisplayType && currentErrorCount) {
                const currentPercentage = Math.floor(currentNumber/currentMax*100);
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT', [currentNumber, currentDisplayType, currentMax, currentPercentage, currentErrorCount])}</SMCLabel>
                    <LinearProgress style={{ width: '600px' }} variant="determinate" value={currentPercentage} />
                    </>
                );
            } else if (hasValue(currentNumber) && currentMax && currentDisplayType) {
                const currentPercentage = Math.floor(currentNumber/currentMax*100);
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT_NOERR', [currentNumber, currentDisplayType, currentMax, currentPercentage])}</SMCLabel>
                    <LinearProgress style={{ width: '600px' }} variant="determinate" value={currentPercentage} />
                    </>
                )
            } else if (hasValue(currentNumber) && currentDisplayType && currentErrorCount) {
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT_NOMAX', [currentDisplayType, currentNumber, currentErrorCount])}</SMCLabel>
                    <SMCLabel>{t('SMC_PROGRESS_WAIT')}</SMCLabel>
                    </>
                );
            } else if (hasValue(currentNumber) && currentDisplayType) {
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT_NOMAX_NOERR', [currentDisplayType, currentNumber])}</SMCLabel>
                    <SMCLabel>{t('SMC_PROGRESS_WAIT')}</SMCLabel>
                    </>
                );
            }
        }
        else if (isFetchingSummary) {
            return (
                <SMCPageLoading />
                // <RowLayout>{t('SMC_SYSOPS_EXPWIZ_DO_DONW_FETCH')} <img src={loadingSpinner} alt="" style={{ height: 28, width: 28 }} /></RowLayout>
            );
        }
        else if (processedObjectDataList || warningsOccurredDataList) {
            return (
                <>
                {
                    processedObjectDataList && processedObjectDataList.length && (
                    <>
                    <p>{t('SMC_IMPORT_SUMMARY_PROCESSED')}</p>
                    <table width="500px">
                        <tbody>
                            <tr className="WizTableHead">
                                <td align="center" width="120">Type</td><td align="center" width="95">Created</td><td align="center" width="95">Skipped</td><td align="center" width="95">Failed</td><td align="center" width="95">Total</td>
                            </tr>
                            {
                                processedObjectDataList.map((obj, index) => (
                                    <tr className="WizTableSmallRow" key={index}>
                                        <td>{obj.type}</td>
                                        <td align="center">{obj.created}</td>
                                        <td align="center">{obj.skipped}</td>
                                        <td align="center">{obj.failed}</td>
                                        <td align="center">{obj.total}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                    </>
                    )
                }
                {
                    warningsOccurredDataList && warningsOccurredDataList.length && (
                        <>
                        <div style={{ display: 'block', height: 20 }} />
                        <p>{t('SMC_SYSOPS_IMPWIZ_DO_DONE_TEXT_WARN')}</p>
                        <table>
                            <tbody>
                                <tr className="WizTableHead">
                                    <td width="30">#</td><td width="50">Type</td><td width="120">Object</td><td width="70">Service Domain</td><td width="230">Message</td>
                                </tr>
                                {
                                    warningsOccurredDataList.map((obj, index) => (
                                        <tr className="WizTableSmallRow" key={index}>
                                            <td>{obj.number}</td>
                                            <td>{obj.type}</td>
                                            <td>{obj.object}</td>
                                            <td>{obj.serviceDomain}</td>
                                            <td>{obj.message}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                     </>
                    )
                }
                </>
            );
        }
        else {
            return null;
        }
    }, [hasError, errorMessage, inProgress, stopped, currentNumber, currentMax, currentDisplayType, currentErrorCount, isFetchingSummary, processedObjectDataList, warningsOccurredDataList, t]);

    return (
        <>
        <SMCModalBody lineHeight="24px">
            <div id="import-summary">
                <h2>{headerText}</h2>
                { body }
            </div>
        </SMCModalBody>
        <SMCModalButtonContainer>
            {
                inProgress && (
                    <SMCButton disabled={!taskId} onClick={handleStop}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="stop" />
                        {t('SMC_GEN_STOP')}
                    </SMCButton>
                )
            }
            <SMCButton onClick={handleDismiss}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                {t('SMC_GEN_CLOSE')}
            </SMCButton>
            {
                (processedObjectDataList || warningsOccurredDataList) && (
                    <SMCButton onClick={() => exportSummaryToExcel('import-summary', 'import_status.xls')}>Export to Excel</SMCButton>
                )
            }
        </SMCModalButtonContainer>
        </>
    )
}

export default ImportSMPStepTwo;